/**
 * Shared dashboard stats for home widgets and PriorityCockpit.
 * Single source for counts, lists, and format helpers.
 */

import { useMemo, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { refreshPrioritizationDashboardAggregates, type RefreshOptions } from '../../lib/refreshPrioritizationDashboardAggregates';
import {
  itemIsAwaitingTriage,
  itemLifecycleStage,
  triageFinalizedOutOfQueueIds,
} from '../../lib/intakeQueueVisibility';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { usePrioritizationTaskStore } from '../../stores/prioritization/prioritizationTaskStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { useCatchballLoopStore } from '../../stores/prioritization/catchballLoopStore';
import { useProposalPacketStore } from '../../stores/prioritization/proposalPacketStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';

/** Rows carry the exit status under one of two spellings; 'active' when neither is set. */
type ExitStatusCarrier = { exit_status?: string | null; exitStatus?: string | null };
function exitStatusOf(c: ExitStatusCarrier): string {
  return c.exit_status ?? c.exitStatus ?? 'active';
}

/** @deprecated renamed to isActiveItem. */
function needsShaping(
  c: { lifecycle_state?: string; exit_status?: string; exitStatus?: string; id?: string },
  priorityIdsSet: Set<string>
): boolean {
  const exitStatus = exitStatusOf(c);
  if (exitStatus === 'archived' || exitStatus === 'deferred') return false;
  if (c.id && priorityIdsSet.has(c.id)) return false;
  return c.lifecycle_state === 'item';
}

export function formatTime(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function useDashboardStats() {
  const items = useItemsStore((s) => s.items);
  // lastSyncAt is null until the first successful fetch completes.
  // We use it to distinguish "items = [] because nothing fetched yet" from
  // "items = [] because the workspace is genuinely empty". Without this,
  // the empty state flashes on every page load before the first fetch runs.
  const itemsLastSyncAt = useItemsStore((s) => s.syncState.lastSyncAt);
  /** Workspace id stamped on the last successful items fetch — invalid when tenant workspace changes before refetch completes. */
  const itemsFetchWorkspaceId = useItemsStore((s) => s.itemsFetchWorkspaceId);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceResolutionComplete = useTenantStore((s) => s.workspaceResolutionComplete);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const tracks = useTrackStore((s) => s.tracks);
  const sessions = useSessionStore((s) => s.sessions);
  const allTasks = usePrioritizationTaskStore((s) => s.tasks);
  const userId = useUserStore((s) => s.userId);

  const attributes = useAttributesStore((s) => s.attributes);
  const loops = useCatchballLoopStore((s) => s.loops);
  const proposals = useProposalPacketStore((s) => s.proposals);
  const edges = useItemRelationshipsStore((s) => s.edges);
  const assessments = useTriageStore((s) => s.assessments, shallow);

  const refreshAllData = useCallback((options?: RefreshOptions) => {
    refreshPrioritizationDashboardAggregates(options);
  }, []);

  const priorityIdsSet = useMemo(() => new Set(itemPools.flatMap((l) => l.priorityIds)), [itemPools]);

  const pendingTasks = useMemo(() => {
    if (!allTasks || !Array.isArray(allTasks)) return [];
    return allTasks.filter(
      (t) =>
        t?.participantIds?.includes(userId) &&
        t?.status === 'open' &&
        !t?.responses?.some((r) => r.participantId === userId)
    );
  }, [allTasks, userId]);

  const stats = useMemo(() => {
    const triagedOut = triageFinalizedOutOfQueueIds(assessments);

    const pendingItems = items.filter((c) => exitStatusOf(c as unknown as ExitStatusCarrier) === 'active').length;
    const shapingItems = items.filter((c) => needsShaping(c, priorityIdsSet)).length;
    const needsLeveling = items.filter((c) => c.altitude === undefined || c.altitude === null).length;
    const activeCyclesList = cycles?.filter((c: { status: string }) => c.status === 'active') || [];
    const cycleNameById = new Map((cycles ?? []).map((c: { id: string; name: string }) => [c.id, c.name]));
    const activeTracksList = tracks
      .filter((t) => t.status === 'active')
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .map((t) => ({
        id: t.id,
        name: t.name,
        cycleId: t.cycleId,
        cycleName: t.cycleId ? (cycleNameById.get(t.cycleId) ?? null) : null,
        isStandalone: t.isStandalone,
      }));
    const publishedLists = itemPools.filter((l) => l.status === 'accepted' || l.status === 'incorporated');

    // Intake queue — same rows as /prioritization/intake/triage (not "all lifecycle_stage intake")
    const intakeItems = items.filter((c) => itemIsAwaitingTriage(c, triagedOut));

    // Scoring gaps — active items missing required attribute values
    const requiredAttrs = attributes.filter((a) => a.required);
    const itemsWithScoringGaps = requiredAttrs.length > 0
      ? items.filter((c) => {
          if (itemLifecycleStage(c) === 'intake') return false;
          const exitStatus = exitStatusOf(c as unknown as ExitStatusCarrier);
          if (exitStatus === 'archived' || exitStatus === 'deferred') return false;
          return requiredAttrs.some((attr) => {
            const val = c.attributes?.find((a: { attributeKey?: string; value?: unknown }) => a.attributeKey === attr.key)?.value;
            return val === undefined || val === null || val === '';
          });
        })
      : [];

    // needsValues = items missing at least one required attribute value (same as scoring gaps)
    const needsValues = itemsWithScoringGaps.length;

    // Readiness blockers — active items missing required values (same pool as scoring gaps)
    const rawItems = itemsWithScoringGaps;

    // Unresolved dependency edges (active, not dissolved)
    const activeDependencyEdges = edges.filter((e) => e.type === 'dependency' && e.status !== 'dissolved');

    // Upcoming sessions (next 14 days)
    const now = new Date();
    const twoWeeksOut = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const scheduledAtOf = (s: unknown): string | Date | undefined =>
      (s as { scheduledAt?: string | Date }).scheduledAt;
    const upcomingSessions = sessions.filter((s) => {
      const at = scheduledAtOf(s);
      const scheduled = at ? new Date(at) : null;
      return scheduled && scheduled > now && scheduled < twoWeeksOut && s.state !== 'PUBLISHED';
    }).sort((a, b) => {
      const aDate = new Date(scheduledAtOf(a) ?? 0).getTime();
      const bDate = new Date(scheduledAtOf(b) ?? 0).getTime();
      return aDate - bDate;
    });

    // Active catchball loops — scoped to cycles in the current workspace, and only
    // where the current user is the ball holder (avoids showing loops from other
    // workspaces when the store has fetched without a workspace filter).
    const workspaceCycleIds = new Set((cycles ?? []).map((c) => c.id));
    const activeLoops = loops.filter(
      (l) =>
        l.state !== 'closed' &&
        l.state !== 'DECLINED' &&
        l.state !== 'ACCEPTED' &&
        l.state !== 'FINAL_REJECTED' &&
        workspaceCycleIds.has(l.cycleId) &&
        l.ballHolderUserId === userId
    );

    // Proposals awaiting this person's decision. The live status is 'proposed'
    // ('pending' never occurred on production); addressed to me, or to a cycle
    // I own. The Next-Step Engine applies the same rule (migration 820).
    const ownedCycleIds = new Set((cycles ?? []).filter((c) => c.ownerId === userId).map((c) => c.id));
    const pendingProposals = proposals.filter(
      (p) =>
        p.status === 'proposed' &&
        p.proposedBy !== userId &&
        ((p.targetRecipients ?? []).includes(userId) || ownedCycleIds.has(p.cycleId)),
    );

    // Show skeleton until workspace is resolved and items have completed at least one
    // successful sync (lastSyncAt set). Do not key off itemsIsLoading after that: Layout
    // may call fetchFromSupabase multiple times on login (hydration + workspace effect),
    // and toggling isLoading would blank the whole dashboard on each refetch.
    //
    // !workspaceResolutionComplete keeps the skeleton until ensureWorkspaceContext().finally()
    // fires so we don't flash zero-state before tenant context exists.
    //
    // If items were fetched under a different workspace id than the store's current workspace
    // (hint vs resolved context race), keep skeleton until the matching refetch lands — avoids
    // one frame of empty-state or wrong counts before the correct slice loads.
    //
    // switchTenant() sets currentWorkspaceId=null before re-resolving the workspace on every
    // init, causing currentWorkspaceId to briefly be null 1–3× after the first successful
    // fetch. If we have valid cached data (itemsLastSyncAt + itemsFetchWorkspaceId set), treat
    // that null flash as a harmless transition rather than a loading state — prevents the
    // Getting Started / populated dashboard from flickering back to the skeleton on each fire.
    const hasValidCachedData = Boolean(itemsLastSyncAt) && Boolean(itemsFetchWorkspaceId);
    const itemsWorkspaceStale =
      Boolean(currentWorkspaceId) &&
      Boolean(itemsLastSyncAt) &&
      itemsFetchWorkspaceId !== currentWorkspaceId;
    const isLoading =
      !workspaceResolutionComplete ||
      (!currentWorkspaceId && !hasValidCachedData) ||
      !itemsLastSyncAt ||
      itemsWorkspaceStale;

    return {
      isLoading,
      totalItems: items.length,
      pendingItems,
      shapingItems,
      needsValues,
      needsLeveling,
      activeCycles: activeCyclesList.length,
      activeCyclesList,
      activeTrackCount: activeTracksList.length,
      activeTracksList,
      publishedLists,
      recentSessions: sessions.slice(0, 5),
      pendingTaskCount: pendingTasks.length,
      pendingTasks,
      // New
      intakeItems,
      intakeCount: intakeItems.length,
      itemsWithScoringGaps,
      scoringGapCount: itemsWithScoringGaps.length,
      rawItems,
      rawItemCount: rawItems.length,
      activeDependencyEdges,
      unresolvedDependencyCount: activeDependencyEdges.length,
      upcomingSessions,
      activeLoops,
      activeLoopCount: activeLoops.length,
      pendingProposals,
      pendingProposalCount: pendingProposals.length,
      refreshAllData,
    };
  }, [
    items,
    assessments,
    itemsLastSyncAt,
    itemsFetchWorkspaceId,
    currentWorkspaceId,
    workspaceResolutionComplete,
    cycles,
    tracks,
    itemPools,
    sessions,
    priorityIdsSet,
    pendingTasks,
    attributes,
    edges,
    loops,
    proposals,
    userId,
    refreshAllData,
  ]);

  return stats;
}
